import React, { useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import eventClickBannerCoupon from 'ui/lib/gtm/eventClickBannerCoupon';
import useIsElementVisible from 'ui/hooks/useIsElementVisible';
import resizeBannerPackageCouponService from '../../../helpers/utils/resizeBannerPackageCouponService';
import trimText from '../../../helpers/utils/trimText';
import { useSelector } from 'react-redux';

const ServiceBannerSection = ({
  bannerId,
  bannerTitle,
  bannerDesc,
  bannerImg,
  bannerAltImg,
  bannerHeight,
  bannerWidth,
  bannerLink,
  handleCollectDataLayer,
  typeService,
  category,
  styleMy = 'my-20',
  isForAccList = false,
}: {
  bannerId?: string;
  bannerTitle?: string;
  bannerDesc?: string;
  bannerImg?: string;
  bannerAltImg?: string;
  bannerHeight?: number;
  bannerWidth?: number;
  bannerLink?: string;
  handleCollectDataLayer?: ({
    id,
    name,
    creative,
    position,
  }: {
    id: string;
    name: string;
    creative: string;
    position: string;
  }) => void;
  typeService?: string;
  category?: string;
  styleMy?: string;
  isForAccList?: boolean;
}) => {
  const linkPath = bannerLink ? new URL(bannerLink) : null;
  const { isMobileScreen } = useSelector((state: any) => state.page);

  const [isViewportChanged, setIsViewportChanged] = useState(false);

  const initialWidth = useRef(null);

  const [imgSize, setImgSize] = useState<{
    height: number;
    width: number;
  }>({
    height: 0,
    width: 0,
  });

  const titleBannerGTM = useMemo(() => {
    return trimText(bannerTitle);
  }, [bannerTitle]);

  const ref = useIsElementVisible(
    () =>
      handleCollectDataLayer({
        id: bannerId || 'not available',
        name: titleBannerGTM?.toLowerCase() || 'not available',
        creative: linkPath ? linkPath.pathname : 'not available',
        position: `1 - ${typeService} servis`,
      }),
    [category]
  );

  useEffect(() => {
    const img = new window.Image();
    img.src = bannerImg;
    img.onload = () => {
      setImgSize({
        height: img.naturalHeight,
        width: img.naturalWidth,
      });
    };
  }, [bannerImg]);

  useEffect(() => {
    const handleResize = () => {
      const currentWidth = window.innerWidth;

      if (initialWidth.current === null) {
        initialWidth.current = currentWidth;
      }

      if (currentWidth !== initialWidth.current) {
        setIsViewportChanged(true);
      } else {
        setIsViewportChanged(false);
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return (
    <section ref={ref} className={`mt-[10px] lg:mt-[20px] lg:mb-${isForAccList ? '0' : '[32px]'}`}>
      <div className="container xl:px-0">
        <h1 className="text-base font-bold uppercase text-reliableBlack lg:text-2xl lg:mb-2">
          {bannerTitle}
        </h1>
        <p className="text-xs font-small leading-relaxed text-reliableBlack mb-2 lg:text-base lg:font-small lg:mb-5">
          {bannerDesc}
        </p>
      </div>
      {bannerImg ? (
        <div className="lg:container xl:px-0">
          <Link
            onClick={() => {
              eventClickBannerCoupon(
                {
                  id: bannerId ? bannerId : 'not available',
                  link: linkPath ? linkPath.pathname : null,
                  title: titleBannerGTM,
                  typeService: typeService,
                },
                category
              );
            }}
            href={bannerLink || '#'}
            className="flex justify-center items-center"
          >
            <Image
              alt={bannerAltImg}
              width={
                isViewportChanged
                  ? resizeBannerPackageCouponService(isMobileScreen, imgSize.width, 'width')
                  : bannerWidth
              }
              height={
                isViewportChanged
                  ? resizeBannerPackageCouponService(isMobileScreen, imgSize.height, 'height')
                  : bannerHeight
              }
              className={`object-contain max-[768px]:aspect-ratio max-[768px]:aspect-video object-center min-[768px]:max-h-[${imgSize.height < 450 ? imgSize.height : 450}px] ${imgSize.width && `min-[768px]:max-w-[${imgSize.width}px]`}`}
              priority={true}
              src={bannerImg}
            />
          </Link>
        </div>
      ) : (
        <div className="container xl:px-0">
          <div className="flex justify-center items-center w-full">
            <span className={`loader mx-auto ${styleMy}`} />
          </div>
        </div>
      )}
    </section>
  );
};

export default ServiceBannerSection;
